import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import {
    alarmCols,
    shouldDisableCell,
    generateColums,
    nestedHeaders,
    ChannelSheetCol,
    getColumnTitle,
    simpleViewColumnsSet,
    systemGroupSimpleColsSet,
    leadingZeroNumericColSet,
    normalizeLeadingZeros,
    isAlarmPanelSyncCol,
    clearByChannelType,
} from '@models/channel-sheet';
import { ChannelExcelService } from '@service/channel/channel-excel.service';
import { ChannelSheetService } from '@service/channel/channel-sheet.service';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import jspreadsheet from 'jspreadsheet-ce';
import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { DialogType } from '../dialog-channel-category/dialog-channel-category.component';
import { DialogChannelGroupComponent } from '../dialog-channel-group/dialog-channel-group.component';
import { ChannelGroupService } from '@service/channel/channel-group.service';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { MatDialog } from '@angular/material/dialog';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { ChannelPanel } from '../side-menu/side-menu.component';
import { VesselService } from '@service/vessel.service';
import { ChannelSheetStylesService } from '@service/channel/channel-sheet-styles.service';
import { generateExcelRow, parseExcelRow } from '@service/channel/channel-utils';
import { PermissionGuardService } from '@service/permission-guard.service';
import { ChannelType, ChannelTypeStrings, isBlank } from 'icreate/common/models/channel-type';
import { TranslateService } from '@ngx-translate/core';
import { ChannelService } from '@service/channel/channel.service';
import { DialogInsertRowsComponent } from '../dialog-insert-rows/dialog-insert-rows.component';
import { PrintDialogComponent, PrintPageType, PrintType } from '@components/print-dialog/print-dialog.component';
import { PrintChannelsService } from '@service/print-channels-service/print-channels.service';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { systemChannelGroupNum } from 'icreate/common/models/system-channels';
import { ChannelExcelValidator } from 'icreate/common/services/channel-excel-validator';
import { ExcelCellRowIndex, ExcelParsedProperties } from 'icreate/common/models/channel-config';
import {
    CHANNEL_LENGTH_VALIDATION,
    CHANNEL_RANGE_VALIDATION,
    CHANNEL_VALIDATION,
} from 'icreate/common/models/channel-validate';
import { SheetErrorType } from '@models/sensor-cable-tag-sheet';
import { ChannelSheetUtilsService } from '@service/channel/channel-sheet-utils.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { channelsNeedUpdate } from '@service/channel/channel-excel-utils';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { Vessel } from 'icreate/common/models/vessel';

export enum ExcelSheetKeyEvent {
    ARROW_RIGHT = 'ArrowRight',
    ARROW_LEFT = 'ArrowLeft',
    ARROW_DOWN = 'ArrowDown',
    ARROW_UP = 'ArrowUp',
}

export interface CellError {
    id: number;
    error: SheetErrorType;
    columnName?: string;
    columnNames?: string;
}

@Component({
    selector: 'app-channel-sheet',
    templateUrl: './channel-sheet.component.html',
    styleUrls: ['./channel-sheet.component.scss'],
    standalone: false,
})
export class ChannelSheetComponent implements AfterViewInit, OnDestroy {
    readonly TABLE_HEADER_HEIGHT = 72;
    readonly TOOLBAR_HEIGHT = 46;
    readonly CHANNEL_GROUP_HEADER_HEIGHT = 40;
    readonly MAX_CHANNEL_ROWS = 500;
    readonly ERROR_ICON_RIGHT = 12;

    readonly SIMPLE_VIEW = ViewMode.SIMPLE_VIEW;
    readonly VIEW_ALL = ViewMode.VIEW_ALL;
    readonly ADD_CHANNEL_GROUP = DialogType.add;
    readonly excelImportError = 'excel-channel-import.errors';

    readonly ERROR_REQUIRED = CHANNEL_VALIDATION.REQUIRED;
    readonly ERROR_NUMERIC = CHANNEL_VALIDATION.NUMERIC;
    readonly ERROR_VALUE_LENGTH = CHANNEL_VALIDATION.VALUE_LENGTH;
    readonly ERROR_VALUE_RANGE = CHANNEL_VALIDATION.VALUE_RANGE;
    readonly ERROR_RANGE = CHANNEL_VALIDATION.RANGE;
    readonly ERROR_OUT_OF_RANGE = CHANNEL_VALIDATION.OUT_OF_RANGE;
    readonly ERROR_NOT_INTEGER = CHANNEL_VALIDATION.NOT_INTEGER;

    readonly INFO_MESSAGE = PanelState.Info;
    readonly ALARM_MESSAGE = PanelState.Alarm;
    readonly ERROR_MESSAGE = PanelState.Error;

    @Input() vessel: Vessel;
    @Input() userCanEdit: boolean = true;
    @Output() pageUpdated = new EventEmitter<boolean>();

    @ViewChild('spreadsheet') spreadsheet: ElementRef;
    worksheets: jspreadsheet.JspreadsheetInstance;

    @ViewChild('groupDialog') groupDialog: DialogChannelGroupComponent;
    @ViewChild('channelSheetMenu') channelSheetMenu: ElementRef;
    @ViewChild('excelFileInput') excelFileInput!: ElementRef;
    @ViewChild('simpleFileInput') simpleFileInput!: ElementRef;
    @ViewChild('infoMessage') infoMessage: ElementRef;
    @ViewChild('alarmDialog') alarmDialog: ElementRef;
    @ViewChild('importErrorDialog') importErrorDialog: ConfirmationWindowComponent;
    @ViewChild('errorsContainer') errorsContainer: ElementRef;
    @ViewChild('tabScrollContainer', { static: true }) tabScrollContainer!: ElementRef;
    @ViewChild('addChannelGroupButton') addChannelGroupButton!: ElementRef;
    @ViewChild('viewModeContainer') viewModeContainer!: ElementRef;
    @ViewChild('helperContainer') helperContainer!: ElementRef;
    @ViewChild('alarmLevelsContainer') alarmLevelsContainer!: ElementRef;

    channelCategoryList: ChannelCategory[] = [];
    channelGroupList: ChannelGroup[] = [];
    prevSelectedChannelCoord: { x1: number; x2: number; y1: number; y2: number };
    insertRowsSelectedCoord: { x1: number; x2: number; y1: number; y2: number };
    activeGroup: ChannelGroup;
    newGroup: Array<{ sheetName: string }> = [];
    selectedFile: File;
    channelRows: any[][] = null;
    columns = [];
    alarmColumns: string[];
    viewMode = this.SIMPLE_VIEW;
    selectedAlarmKeys = new Map();
    selectedChannelId: string;
    isAlarmUpdated: boolean = false;
    isInfoMessageVisible: boolean = false;
    isAlarmDialogVisible: boolean = false;
    isChannelUpdated: boolean;
    tabState: ChannelPanel;

    initialChannelData;
    initalChannelRow;

    keyPressEventString: string;

    textColorIsOpen: boolean = false;
    textColor: string;
    fillColorIsOpen: boolean = false;
    fillColor: string;
    undoChangedChannelTypeInfo: any[] = [];
    redoChangedChannelTypeInfo: any[] = [];
    currentIndex: number = 0;
    importErrorMessage: string = '';

    hasCellError: boolean = false;
    showErrors: boolean = false;
    cellErrors: CellError[] = [];

    errorIconRight: string;
    errorIconLeft: string;

    isAutoSave: boolean = false;

    private disableCellsDebounceTimer: any = null;
    private destroySubscriptionSubject$ = new Subject<void>();
    private reloadSheetVersion = 0;
    isValidationEnabled: any;

    currentPanel: PanelState = PanelState.None;

    private pendingDisableRows = new Set<number>();
    constructor(
        private vesselService: VesselService,
        private channelExcelService: ChannelExcelService,
        private channelSheetService: ChannelSheetService,
        private excelStylesService: ChannelSheetStylesService,
        private channelGroupService: ChannelGroupService,
        private channelCategoryService: ChannelCategoryService,
        private toastr: ToastrService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private permissionGuardService: PermissionGuardService,
        private translateSerivce: TranslateService,
        private channelService: ChannelService,
        private printChannelsService: PrintChannelsService,
        private channelSheetUtilsService: ChannelSheetUtilsService,
        private messageDialogService: MessageDialogService
    ) {}

    @HostListener('keydown', ['$event'])
    async handleCopy(event: KeyboardEvent) {
        if ((event.ctrlKey || event.metaKey) && event.key === 's') {
            event.preventDefault();
            if (this.isChannelUpdated) {
                await this.save();
            }
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        this.updateTableWidth();
    }

    @HostListener('mousedown', ['$event'])
    async handleClickOut(event: MouseEvent) {
        const sideMenuElement = this.channelSheetService.getSideMenu();
        if (!this.prevSelectedChannelCoord) return;
        if (
            sideMenuElement.nativeElement.contains(event.target) ||
            this.viewModeContainer.nativeElement.contains(event.target) ||
            this.alarmLevelsContainer.nativeElement.contains(event.target) ||
            this.helperContainer.nativeElement.contains(event.target)
        ) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (!this.infoMessage?.nativeElement.contains(event.target)) {
            this.currentPanel = PanelState.None;
        }
        if (!this.alarmDialog.nativeElement.contains(event.target)) {
            this.currentPanel = PanelState.None;
        }

        if (this.showErrors && !this.errorsContainer.nativeElement.contains(event.target)) {
            this.showErrors = false;
        }
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
        this.channelGroupService.setList([]);
        this.channelCategoryService.setList([]);
        this.channelSheetService.setActiveGroup(undefined);
        this.channelSheetService.setSelectedChannel(undefined);
        this.channelSheetService.setIsAlarmUpdate(false);
        this.channelSheetService.setIsChannelUpdated(false);

        document.removeEventListener('mousedown', this.sideMenuMouseDown);
        this.spreadsheet.nativeElement.removeEventListener('keydown', this.arrowKeyPress);
        this.spreadsheet.nativeElement.removeEventListener('keydown', this.handleKeyDown);
        this.spreadsheet.nativeElement.removeEventListener('scroll', this.fixedHeaderWhileScrolling);
        this.tabScrollContainer.nativeElement.removeEventListener('wheel', this.tabScrollWheelHandler);
    }

    ngOnInit(): void {
        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(canEdit => {
            this.userCanEdit = canEdit;
            this.updateSheetReadOnlyState();
        });
    }

    async ngAfterViewInit() {
        this.columns = generateColums();
        this.alarmColumns = Object.keys(alarmCols);

        this.alarmColumns.forEach(key => {
            this.selectedAlarmKeys.set(key, alarmCols[key]);
        });

        this.initSheet();

        this.tabScrollContainer.nativeElement.addEventListener('wheel', this.tabScrollWheelHandler, {
            passive: false,
        });

        // Setting scroll
        this.spreadsheet.nativeElement.prepend(this.channelSheetMenu.nativeElement);
        this.spreadsheet.nativeElement.addEventListener('keydown', this.arrowKeyPress);
        this.spreadsheet.nativeElement.addEventListener('keydown', this.handleKeyDown);
        this.spreadsheet.nativeElement.addEventListener('scroll', this.fixedHeaderWhileScrolling);
        document.addEventListener('mousedown', this.sideMenuMouseDown);

        this.channelSheetService.tabState$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(tabState => {
            this.tabState = tabState;
            if (this.tabState === ChannelPanel.ALARM) {
                this.updateSaveButton();
                const coord = this.prevSelectedChannelCoord;
                if (coord && this.worksheets && this.channelRows?.[coord.y1]) {
                    this.worksheets.updateSelectionFromCoords(coord.x1, coord.y1, coord.x2, coord.y2);
                    this.channelSheetService.setSelectedChannel(this.channelRows[coord.y1]);
                }
            }
        });

        this.channelGroupService.list$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(channelGroupList => {
            this.channelGroupList = channelGroupList;

            if (!this.activeGroup) {
                this.channelSheetService.setActiveGroup(this.channelGroupList[0]);
                this.channelSheetService.setSelectedChannel(undefined);
            } else if (this.activeGroup?.vesselId !== this.channelGroupList[0]?.vesselId) {
                this.channelSheetService.setActiveGroup(this.channelGroupList[0]);
                this.channelSheetService.setSelectedChannel(undefined);
            }
        });

        this.channelSheetService.activeGroup$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async activeGroup => {
                if (activeGroup === this.activeGroup) {
                    await this.reloadSheet();
                    if (this.selectedChannelId && this.prevSelectedChannelCoord) {
                        const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
                        this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
                        this.channelSheetService.setSelectedChannel(this.channelRows[y1]);
                    }
                    return;
                }
                this.activeGroup = activeGroup;
                this.channelSheetService.setSelectedChannel(undefined);
                await this.reloadSheet();
            });

        this.channelCategoryService.list$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(channelCategoryList => {
                this.channelCategoryList = channelCategoryList;
                this.channelSheetService.setSelectedChannel(undefined);
            });

        this.channelSheetService.selectedChannel$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(selectedChannel => {
                if (!selectedChannel) {
                    this.selectedChannelId = undefined;
                    if (this.prevSelectedChannelCoord?.x1 || this.prevSelectedChannelCoord?.x1 == 0) {
                        this.prevSelectedChannelCoord = null;
                        this.worksheets.resetSelection(false);
                    }
                    return;
                }
                if (selectedChannel.rowId) {
                    this.selectedChannelId = selectedChannel.rowId;
                } else {
                    const channel = parseExcelRow(selectedChannel);
                    this.selectedChannelId = channel.rowId;
                }
            });

        this.channelSheetService.isChannelUpdated$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(isChannelUpdated => {
                this.isChannelUpdated = isChannelUpdated;
                this.pageUpdated.emit(isChannelUpdated);
            });

        this.channelSheetService.isAlarmUpdated$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(isAlarmUpdated => {
                this.isAlarmUpdated = isAlarmUpdated;
            });

        this.channelSheetService.updateChannel$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(updatedChannel => {
                if (!this.prevSelectedChannelCoord || !this.worksheets) return;
                const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
                if (!isNaN(y1)) {
                    const channelRow: any[] = this.channelRows[y1];
                    const selectedChanelId = this.selectedChannelId;
                    if (
                        selectedChanelId?.toString() === channelRow[ChannelSheetCol.INDEX]?.toString() &&
                        updatedChannel?.rowId
                    ) {
                        const channel: any = generateExcelRow(updatedChannel);
                        channel[0] = channel[0].toString();
                        channel[1] = channel[1] ? Number(channel[1]) : channel[1];
                        (this.worksheets as any).ignoreHistory = true;
                        for (let i = 0; i < this.channelRows[y1].length; i++) {
                            if (!_.isEqual(this.channelRows[y1][i], channel[i])) {
                                const cell = this.worksheets.getCellFromCoords(i, y1);
                                if (cell) {
                                    this.worksheets.setValueFromCoords(i, y1, channel[i], true);
                                }
                            }
                        }
                        (this.worksheets as any).ignoreHistory = false;
                        this.channelRows[y1] = channel;
                        this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
                    }
                }
            });

        this.updateHiddenColumn();
        this.cdr.detectChanges();
    }

    isImportButtonDisabled() {
        return !this.userCanEdit;
    }

    private sideMenuMouseDown = (event: MouseEvent) => {
        const sideMenu = this.channelSheetService.getSideMenu();
        if (!sideMenu?.nativeElement.contains(event.target as Node)) return;
        if (!this.prevSelectedChannelCoord || !this.worksheets) return;

        const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
        this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
    };

    arrowKeyPress = (event: KeyboardEvent) => {
        if (event.key === ExcelSheetKeyEvent.ARROW_RIGHT) {
            const tr = this.worksheets.getSelectedRows()[0] as HTMLElement;
            const td = (tr as HTMLElement).childNodes[this.worksheets.getSelectedColumns()[0] + 1] as HTMLElement;

            const { width: currentPosition } = (event.target as HTMLElement).getBoundingClientRect();
            const { width, right: outOfPosition } = td.nextElementSibling.getBoundingClientRect();

            if (outOfPosition >= currentPosition) {
                (event.target as HTMLElement).scrollBy({
                    left: width + (outOfPosition - currentPosition),
                });
            }
            this.keyPressEventString = ExcelSheetKeyEvent.ARROW_RIGHT;
        } else if (event.key === ExcelSheetKeyEvent.ARROW_LEFT) {
            const tr = this.worksheets.getSelectedRows()[0] as HTMLElement;
            const td = (tr as HTMLElement).childNodes[this.worksheets.getSelectedColumns()[0] + 1] as HTMLElement;

            const currentPosition = 0;
            const { width, left: outOfPosition } = td.previousElementSibling.getBoundingClientRect();

            if (outOfPosition <= currentPosition) {
                (event.target as HTMLElement).scrollBy({
                    left: -width + outOfPosition,
                });
            }

            this.keyPressEventString = ExcelSheetKeyEvent.ARROW_LEFT;
        } else if (event.key === ExcelSheetKeyEvent.ARROW_DOWN) {
            const tr = (this.worksheets.getSelectedRows()[0] as HTMLElement).nextElementSibling;
            const td = (tr as HTMLElement).childNodes[this.worksheets.getSelectedColumns()[0] + 1];

            const { height: currentPosition } = (event.target as HTMLElement).getBoundingClientRect();
            const { height, bottom: outOfPosition } = (td as HTMLElement).getBoundingClientRect();

            if (outOfPosition - this.TABLE_HEADER_HEIGHT >= currentPosition) {
                (event.target as HTMLElement).scrollBy({
                    top: height,
                });
            }

            this.keyPressEventString = ExcelSheetKeyEvent.ARROW_DOWN;
        } else if (event.key === ExcelSheetKeyEvent.ARROW_UP) {
            const tr = (this.worksheets.getSelectedRows()[0] as HTMLElement).previousElementSibling;
            const td = (tr as HTMLElement).childNodes[this.worksheets.getSelectedColumns()[0] + 1];

            const { top: currentPosition } = (event.target as HTMLElement).getBoundingClientRect();
            const { height, top: outOfPosition } = (td as HTMLElement).getBoundingClientRect();

            const headerHeight = this.TABLE_HEADER_HEIGHT + this.TOOLBAR_HEIGHT + this.CHANNEL_GROUP_HEADER_HEIGHT;
            if (outOfPosition - headerHeight <= currentPosition) {
                (event.target as HTMLElement).scrollBy({
                    top: -height,
                });
            }

            this.keyPressEventString = ExcelSheetKeyEvent.ARROW_UP;
        }
    };

    handleKeyDown = (event: KeyboardEvent): void => {
        if (!(event.ctrlKey || event.metaKey)) {
            return;
        }

        const key = event.key.toLowerCase();

        if (key === 'z') {
            event.preventDefault();
            event.stopPropagation();

            if (event.shiftKey) {
                this.handleRedo(this.worksheets);
            } else {
                this.handleUndo(this.worksheets);
            }

            return;
        }

        if (key === 'y') {
            event.preventDefault();
            event.stopPropagation();

            this.handleRedo(this.worksheets);
        }
    };

    fixedHeaderWhileScrolling = event => {
        const header = event.target.querySelectorAll('thead')[0];
        const offset = event.target.scrollTop;
        header.style.transform = `translateY(${offset}px)`;

        const dropDownCloseElement = document.getElementsByClassName('jdropdown-close');
        if (dropDownCloseElement && dropDownCloseElement?.length > 0) {
            ['mousedown', 'mouseup'].forEach(type => {
                (dropDownCloseElement[0] as HTMLElement).dispatchEvent(
                    new MouseEvent(type, { bubbles: true, cancelable: true, view: window })
                );
            });
        }
    };

    scrollToTop() {
        this.spreadsheet.nativeElement.scrollTop = 0;
        const header = this.spreadsheet.nativeElement.querySelector('thead');
        if (header) {
            header.style.transform = 'translateY(0px)';
        }
    }

    tabScrollWheelHandler = (event: WheelEvent) => {
        event.preventDefault();
        this.tabScrollContainer.nativeElement.scrollLeft += event.deltaY;
    };

    keyPressEventMapping = (x1, y1, x2, y2) => {
        // When the keyPress event occurs : Ignore the hidden columns and switch to the visible columns.
        if (
            x1 === x2 &&
            y1 === y2 &&
            (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_RIGHT ||
                this.keyPressEventString === ExcelSheetKeyEvent.ARROW_LEFT)
        ) {
            let selected = this.worksheets.colgroup[x1];
            let count = 0;

            if (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_RIGHT) {
                while (selected && selected.style.visibility === 'collapse') {
                    count += 1;
                    selected = this.worksheets.colgroup[x1 + count];
                }
            } else if (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_LEFT) {
                while (selected && selected.style.visibility === 'collapse') {
                    count -= 1;
                    selected = this.worksheets.colgroup[x1 + count];
                }
            }

            // If there are no selectable cells, revert the selection.
            if (!selected) {
                if (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_RIGHT) {
                    this.worksheets.updateSelectionFromCoords(x1 - 1, y1, x2 - 1, y2);
                } else if (this.keyPressEventString === ExcelSheetKeyEvent.ARROW_LEFT) {
                    this.worksheets.updateSelectionFromCoords(x1 + 1, y1, x2 + 1, y2);
                }
                this.keyPressEventString = '';
                return;
            }

            if (!_.isEqual(this.worksheets.colgroup[x1], selected)) {
                this.worksheets.updateSelectionFromCoords(x1 + count, y1, x2 + count, y2);
                this.arrowKeyPress({
                    key: this.keyPressEventString,
                    target: document.querySelectorAll('.jexcel_container')[0] as EventTarget,
                } as KeyboardEvent);
            }
        }
    };

    async selectionActive(x1: number, y1: number, x2: number, y2: number) {
        this.updateHeaderStyles();
        let channelIdCell = this.worksheets.getCell([0, y1]);
        const channelId = this.worksheets.getValue(channelIdCell).toString();

        if (
            this.selectedChannelId?.toString() !== channelId &&
            !_.isEqual(this.prevSelectedChannelCoord, { x1, y1, x2, y2 }) &&
            this.tabState === ChannelPanel.ALARM
        ) {
            let shouldSetSelectedChannel = true;

            if (this.isAlarmUpdated) {
                const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
                    width: '474px',
                    data: {
                        type: DialogType.description,
                        title: '',
                        description: 'channel.alarm.change-select',
                    },
                });
                shouldSetSelectedChannel = await lastValueFrom(dialogRef.afterClosed());
            }

            this.channelSheetService.showLoading();

            const oldCoords = this.prevSelectedChannelCoord;

            if (this.isAlarmUpdated && shouldSetSelectedChannel) {
                if (oldCoords && this.initalChannelRow) {
                    this.channelRows[oldCoords.y1] = this.initalChannelRow;
                    this.worksheets.setData(this.channelRows);

                    this.channelRows.forEach((row, rowIndex) => {
                        this.checkDisableCells(rowIndex);
                    });
                    this.validateSingleExcelRow(this.channelRows[oldCoords.y1], oldCoords.y1);
                }
            }

            if (shouldSetSelectedChannel) {
                if (oldCoords && this.isAlarmUpdated) {
                    this.validateSingleExcelRow(this.channelRows[oldCoords.y1], oldCoords.y1);
                }

                this.initalChannelRow = structuredClone(this.channelRows[y1]);
                this.channelSheetService.setSelectedChannel(this.channelRows[y1]);
                this.prevSelectedChannelCoord = { x1, y1, x2, y2 };
                this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
                this.updateSaveButton();
            } else {
                if (oldCoords) {
                    this.channelSheetService.setSelectedChannel(this.channelRows[oldCoords.y1]);
                    this.worksheets.updateSelectionFromCoords(oldCoords.x1, oldCoords.y1, oldCoords.x2, oldCoords.y2);
                }
            }
            this.channelSheetService.hideLoading();
        } else if (!_.isEqual(this.prevSelectedChannelCoord, { x1, y1, x2, y2 })) {
            this.prevSelectedChannelCoord = { x1, y1, x2, y2 };
            this.initalChannelRow = structuredClone(this.channelRows?.[y1]);
        }
    }

    async save(autoSave: boolean = false) {
        this.isAutoSave = autoSave;
        let hasError = false;

        if (this.disableCellsDebounceTimer) {
            clearTimeout(this.disableCellsDebounceTimer);
            this.disableCellsDebounceTimer = null;
        }

        if (!this.isAutoSave) this.channelSheetService.showLoading();

        try {
            const currentStyles = this.worksheets.getStyle();
            if (currentStyles) this.excelStylesService.saveStyleDifferences(currentStyles);
        } catch (err) {
            console.error(err);
            if (this.isAutoSave) throw err;
            hasError = true;
        }

        try {
            await this.updateChannels();

            const freshRows = await this.channelExcelService.getExcelChannelRows(
                this.vesselService.getActiveVesselId(),
                this.activeGroup.id
            );
            this.channelRows = freshRows;
            this.initialChannelData = _.cloneDeep(this.channelRows);

            let incomingErrors = [];
            if (this.isValidationEnabled) {
                incomingErrors = ChannelExcelValidator.validateExcelData(freshRows);
            }

            this.channelSheetService.setIsChannelUpdated(false);
            this.channelSheetService.setIsAlarmUpdate(false);

            if (autoSave) return;

            this.worksheets.history = [];
            this.worksheets.historyIndex = -1;
            this.syncWorksheet(this.channelRows);
            this.initialChannelData = _.cloneDeep(this.channelRows);

            this.cellErrors = incomingErrors;
            this.hasCellError = this.cellErrors.length > 0;

            if (this.cellErrors.length > 0) {
                this.updateErrorCellsFormat(this.cellErrors);
            }

            const totalRows = this.channelRows.length;
            let currentRowIndex = 0;
            const chunkSize = 50;

            const processDisableChunks = () => {
                const end = Math.min(currentRowIndex + chunkSize, totalRows);
                for (let i = currentRowIndex; i < end; i++) {
                    this.checkDisableCells(i);
                }
                currentRowIndex = end;
                if (currentRowIndex < totalRows) {
                    requestAnimationFrame(processDisableChunks);
                }
            };
            requestAnimationFrame(processDisableChunks);
            this.messageDialogService.openMessage('channel-sheet.page.updated.message');
        } catch (error) {
            this.hasCellError = true;

            if (error === this.permissionGuardService.permissionError) {
                this.channelRows = _.cloneDeep(this.initialChannelData);

                this.worksheets.ignoreHistory = true;
                this.channelRows.forEach((row, rowIndex) => {
                    row.forEach((value, colIndex) => {
                        this.worksheets.setValueFromCoords(colIndex, rowIndex, value, true);
                    });
                });
                this.worksheets.ignoreHistory = false;
                this.channelSheetService.setIsChannelUpdated(false);
            }

            if (this.isAutoSave) {
                this.channelSheetService.hideLoading();
                throw error;
            }
        } finally {
            this.channelSheetService.hideLoading();
            this.isAutoSave = false;
        }

        return hasError;
    }

    private syncWorksheet(rows: any[][]): void {
        if (!this.worksheets || rows.length === 0) return;
        const originalIgnoreHistory = this.worksheets.ignoreHistory;
        this.worksheets.ignoreHistory = true;

        try {
            this.worksheets.setData(rows);
        } catch (err) {
            console.log(err);
        } finally {
            this.worksheets.ignoreHistory = originalIgnoreHistory;
        }
        this.checkChannelSheetChange(this.worksheets);
    }

    validateChannels() {
        this.worksheets.ignoreHistory = true;

        try {
            this.resetErrorCells();

            const errors = ChannelExcelValidator.validateExcelData(this.channelRows);
            this.cellErrors = errors;
            this.hasCellError = errors.length > 0;

            if (this.cellErrors.length > 0 && !this.isAutoSave) {
                this.updateErrorCellsFormat(this.cellErrors);
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.worksheets.ignoreHistory = false;
        }
    }

    validateSingleExcelRow(rowData: any[], rowIndex: number) {
        this.worksheets.ignoreHistory = true;
        const colLength = Object.keys(ExcelParsedProperties).length - 1;

        for (let i = 1; i < colLength; i++) {
            const cell = this.worksheets.getCell([i, rowIndex]);
            if (cell) {
                cell.classList.remove('error');
                this.worksheets.setComments([i, rowIndex], '', 'Error');
            }
        }

        const currentLiveRow = this.worksheets.getRowData(rowIndex) || rowData;
        if (!currentLiveRow) {
            this.worksheets.ignoreHistory = false;
            return;
        }

        const currentRowId = currentLiveRow[ChannelSheetCol.INDEX];

        if (!this.isValidationEnabled && this.cellErrors) {
            this.cellErrors = this.cellErrors.filter(error => error.id !== currentRowId);
            this.hasCellError = this.cellErrors.length > 0;
            this.worksheets.ignoreHistory = false;
            return;
        }
        const newRowErrors = ChannelExcelValidator.validateSingleRow(currentLiveRow, rowIndex);
        this.cellErrors = this.cellErrors.filter(error => error.id !== currentRowId);

        if (newRowErrors.length > 0) {
            this.cellErrors.push(...newRowErrors);
        }
        this.hasCellError = this.cellErrors.length > 0;

        this.updateErrorCellsFormat(this.cellErrors);

        this.worksheets.ignoreHistory = false;
    }

    prepareErrorsBeforeRender(newChannelRows: any[][]) {
        if (!this.isValidationEnabled || !this.cellErrors.length) {
            return [];
        }

        const updatedErrors = [];

        newChannelRows.forEach((row, rowIndex) => {
            const rowId = row[ChannelSheetCol.INDEX];
            const hasPreviousError = this.cellErrors.some(err => err.id === rowId);

            if (hasPreviousError) {
                const rowErrors = ChannelExcelValidator.validateSingleRow(row, rowIndex);
                if (rowErrors.length > 0) {
                    updatedErrors.push(...rowErrors);
                }
            }
        });

        return updatedErrors;
    }

    async updateChannels() {
        const currentData = this.channelRows;
        if (!_.isEqual(currentData, this.initialChannelData)) {
            let channelObj = { groups: this.activeGroup, channels: currentData };
            await this.channelExcelService.updateChannels(this.vesselService.getActiveVesselId(), channelObj);
        }
    }

    async resetErrorCells() {
        this.worksheets.ignoreHistory = true;

        this.worksheets.rows.forEach((row, idx) => {
            Array.from(row.cells).forEach((cell, cellIdx) => {
                if (cell) {
                    cell.classList.remove('error');
                    const actualX = cell.getAttribute('data-x') || cellIdx;
                    this.worksheets.setComments([Number(actualX), idx], '', 'Error');
                }
            });
        });

        this.worksheets.ignoreHistory = false;
    }

    updateTableWidth() {
        if (!this.spreadsheet) return;
        const tableWidth = document.querySelectorAll('.jexcel_toolbar')[0].clientWidth;

        const scrollBarWidth = 20;

        this.errorIconRight = null;
        this.errorIconLeft = null;

        if (tableWidth > window.innerWidth) {
            const excelContainer = document.querySelectorAll('.jexcel_container')[0];

            if (excelContainer.scrollHeight > excelContainer.clientHeight) {
                this.errorIconRight = this.ERROR_ICON_RIGHT + scrollBarWidth + 'px';
            } else {
                this.errorIconRight = this.ERROR_ICON_RIGHT + 'px';
            }
        } else {
            this.errorIconLeft = tableWidth - this.ERROR_ICON_RIGHT * 3 + 'px';
        }
    }

    focus(x: any, y: any) {
        this.worksheets.updateSelectionFromCoords(x, y);
        const cell = this.worksheets.getCellFromCoords(x, y);

        const excelContainer = document.querySelectorAll('.jexcel_container')[0];

        const tableWidth = excelContainer.clientWidth;
        const tableHeight = excelContainer.clientHeight;

        excelContainer.scrollTo({
            behavior: 'smooth',
            top: cell.offsetTop - (tableHeight - cell.clientHeight) / 2,
            left: cell.offsetLeft - (tableWidth - cell.clientWidth) / 2,
        });
    }

    async updateErrorCellsFormat(errors: any[]) {
        this.updateTableWidth();
        const errorsById = new Map<string, any[]>();
        for (const error of errors) {
            const list = errorsById.get(error.id);
            if (list) {
                list.push(error);
            } else {
                errorsById.set(error.id, [error]);
            }
        }
        this.worksheets.rows.forEach((chRow, idx) => {
            const rowId = chRow.cells.length > 0 ? chRow.cells[1].textContent : null;
            const rowErrors = rowId != null ? errorsById.get(rowId) : null;
            if (rowErrors) {
                rowErrors.forEach(error => {
                    if (!error.columnName && error.columnNames) {
                        error.columnNames.forEach(col => {
                            const cell = chRow.cells[ExcelCellRowIndex[col]];
                            cell.classList.add('error');
                            error.position = {
                                x: cell.getAttribute('data-x'),
                                y: cell.getAttribute('data-y'),
                            };
                        });
                    } else if (error.columnName) {
                        const cell = chRow.cells[ExcelCellRowIndex[error.columnName]];
                        cell.classList.add('error');
                        error.position = {
                            x: cell.getAttribute('data-x'),
                            y: cell.getAttribute('data-y'),
                        };
                    }
                });
            }
        });
    }

    getErrorColumns(error): string[] {
        if (error.columnName) {
            return [error.columnName];
        }

        if (Array.isArray(error.columnNames)) {
            return error.columnNames;
        }

        return [];
    }

    getColumnTitle(columnName: string): string {
        return getColumnTitle(columnName) ?? columnName;
    }

    generateMessage(messageError) {
        let msg = '';
        let option = {};

        const error = messageError.error;
        const tag = messageError.tag;
        const columnName = messageError.columnName ?? messageError.columnNames;
        const alarmType = messageError.alarmType;

        switch (error) {
            case CHANNEL_VALIDATION.NUMERIC:
                msg = this.translateSerivce.instant('channel.validation.errors.numeric', {
                    tag: tag,
                    column: this.getColumnTitle(columnName),
                });
                break;
            case CHANNEL_VALIDATION.REQUIRED:
                msg = this.translateSerivce.instant('channel.validation.errors.required', {
                    tag: tag,
                    column: this.getColumnTitle(columnName),
                });
                break;
            case CHANNEL_VALIDATION.RANGE:
                let colMinTitle = this.getColumnTitle(columnName[0]);
                let colMaxTitle = this.getColumnTitle(columnName[1]);
                const isNormalRange = columnName.some((col: string) => col?.includes('normal range'));
                const isSetPoint = columnName.some((col: string) => col?.includes('set point'));
                if (Array.isArray(alarmType) && alarmType.length >= 2) {
                    colMinTitle = `${alarmType[0]} ${colMinTitle}`;
                    colMaxTitle = `${alarmType[1]} ${colMaxTitle}`;
                } else if (alarmType && typeof alarmType === 'string') {
                    colMinTitle = `${alarmType} ${colMinTitle}`;
                }
                let messageKey = 'channel.validation.errors.less-than';
                if (isSetPoint) {
                    const rangeName = isNormalRange ? 'Normal range' : 'Set point';
                    option = {
                        column: rangeName,
                        tag,
                    };
                    messageKey = 'channel.validation.errors.range-out-of-measurable';
                } else {
                    option = {
                        tag,
                        column: this.getColumnTitle(columnName),
                        colMin: colMinTitle,
                        colMax: colMaxTitle,
                    };
                }
                msg = this.translateSerivce.instant(messageKey, option);
                break;
            case CHANNEL_VALIDATION.OUT_OF_RANGE:
                let displayName = this.getColumnTitle(columnName);
                if (alarmType) {
                    displayName = `${alarmType} ${displayName}`;
                }
                option = {
                    column: displayName,
                    tag,
                };
                msg = this.translateSerivce.instant('channel.validation.errors.out-of-range', option);
                break;
            case CHANNEL_VALIDATION.RANGE_COMPARISON:
                let msgstring = 'channel.validation.errors.range-comparison';
                if (messageError.low) msgstring = 'channel.validation.errors.range-comparison.lower';
                option = {
                    columnHigher: this.getColumnTitle(messageError.messageNames[0]),
                    columnLower: this.getColumnTitle(messageError.messageNames[1]),
                    tag,
                };
                msg = this.translateSerivce.instant(msgstring, option);
                break;
            case CHANNEL_VALIDATION.VALUE_LENGTH:
                option = {
                    min: CHANNEL_LENGTH_VALIDATION[columnName][0],
                    max: CHANNEL_LENGTH_VALIDATION[columnName][1],
                    column: this.getColumnTitle(columnName),
                    tag,
                };
                msg = this.translateSerivce.instant('channel.validation.errors.char-length', option);
                break;
            case CHANNEL_VALIDATION.NOT_INTEGER:
                msg = this.translateSerivce.instant('channel.validation.errors.not-integer', {
                    tag,
                    column: this.getColumnTitle(columnName),
                });
                if (alarmType) {
                    msg += ` ${alarmType}`;
                }
                break;
            case CHANNEL_VALIDATION.VALUE_RANGE:
                option = {
                    min: CHANNEL_RANGE_VALIDATION[columnName][0],
                    max: CHANNEL_RANGE_VALIDATION[columnName][1],
                    tag,
                    column: this.getColumnTitle(columnName),
                };
                msg = this.translateSerivce.instant('channel.validation.errors.value-limit', option);
                break;
            case CHANNEL_VALIDATION.REMARK_LENGTH:
                option = {
                    min: CHANNEL_LENGTH_VALIDATION[columnName][0],
                    max: CHANNEL_LENGTH_VALIDATION[columnName][1],
                    column: this.getColumnTitle(columnName),
                    tag,
                };
                msg = this.translateSerivce.instant('channel.validation.errors.char-length', option);
                break;
            case CHANNEL_VALIDATION.TOTAL_REMARK_LENGTH:
                msg = this.translateSerivce.instant('channel.validation.errors.remarks', {
                    tag,
                });
                break;
        }

        return msg;
    }

    get visibleCellErrors() {
        if (!this.isValidationEnabled) return [];

        if (this.viewMode === ViewMode.VIEW_ALL) {
            return this.cellErrors;
        }

        return this.cellErrors?.filter(error => this.isVisibleError(error));
    }

    get hiddenErrors() {
        if (!this.isValidationEnabled || this.viewMode === ViewMode.VIEW_ALL) {
            return 0;
        }

        return this.cellErrors?.filter(error => !this.isVisibleError(error)).length;
    }

    private isVisibleError(error): boolean {
        if (this.viewMode !== ViewMode.SIMPLE_VIEW) {
            return true;
        }

        const columns = this.getErrorColumns(error);

        return columns?.every(column => {
            const index = ExcelCellRowIndex[column] - 1;
            const result = simpleViewColumnsSet.has(index);
            return result;
        });
    }

    async openGroupDialog() {
        if (!this.userCanEdit) return;
        const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
            width: '474px',
            data: {
                title: 'channel.add-group',
                type: DialogType.add,
            },
        });

        const groupToAdd: ChannelGroup = await lastValueFrom(dialogRef.afterClosed());
        if (groupToAdd) {
            try {
                const newGroup = await this.channelGroupService.add(groupToAdd, this.vesselService.getActiveVesselId());
                const channelList = this.channelSheetService.initChannelRows(newGroup);
                await this.channelService.create(channelList);
                this.channelSheetService.setActiveGroup(newGroup);
            } catch (err) {
                console.error(err);
            } finally {
                this.channelSheetService.hideLoading();
            }
        }
    }

    async reloadSheet() {
        const version = ++this.reloadSheetVersion;
        return this._reloadSheetImpl(version);
    }

    private async _reloadSheetImpl(version: number) {
        if (version !== this.reloadSheetVersion) return;
        this.showErrors = false;
        this.channelSheetService.showLoading();
        let isLoadingData: boolean = false;
        try {
            if (this.activeGroup) {
                this.channelRows = await this.channelExcelService.getExcelChannelRows(
                    this.vesselService.getActiveVesselId(),
                    this.activeGroup.id
                );
                if (version !== this.reloadSheetVersion) return;
                this.initialChannelData = _.cloneDeep(this.channelRows);
            } else {
                this.channelRows = [];
            }
            if (this.channelRows.length > 0) {
                if (!this.worksheets) {
                    await this.reloadSheet();
                    return;
                }
                isLoadingData = true;
                this.worksheets.setData(_.cloneDeep(this.channelRows));
                const currentStyles = this.worksheets.getStyle();
                const savedStyles = await this.excelStylesService.setDefaultStyles(currentStyles, this.activeGroup.id);
                this.channelRows.forEach((row, rowIndex) => {
                    this.checkDisableCells(rowIndex);
                });
                if (savedStyles) {
                    this.worksheets.setStyle(savedStyles);
                }
            } else {
                this.worksheets.setData([]);
            }

            if (this.prevSelectedChannelCoord) {
                const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
                this.channelSheetService.setSelectedChannel(this.channelRows[y1]);
                this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
            }
        } catch (error) {
            console.log(error);
        }

        if (isLoadingData) {
            this.scrollToTop();
            let interval = setInterval(() => {
                const data = this.worksheets.getData();
                if (data.length > 0) {
                    this.channelSheetService.setIsChannelUpdated(true);
                    if (this.isValidationEnabled) this.validateChannels();
                    this.channelSheetService.setIsChannelUpdated(false);
                    this.updateHiddenColumn();
                    this.channelSheetService.hideLoading();
                    clearInterval(interval);
                }
            }, 500);
            return;
        }
        this.scrollToTop();
        this.updateHiddenColumn();
        this.channelSheetService.hideLoading();
        return;
    }

    selectViewMode(viewMode: ViewMode) {
        this.viewMode = viewMode;
        this.updateHiddenColumn();
    }

    updateHiddenColumn() {
        for (let i = 1; i < this.columns.length; i++) {
            if (this.viewMode == this.SIMPLE_VIEW) {
                if (this.isSystemChannelsGroup()) {
                    if (!systemGroupSimpleColsSet.has(i)) this.setColumHiddenOrShow(i, 'collapse');
                    else this.setColumHiddenOrShow(i, 'visible');
                } else {
                    if (!simpleViewColumnsSet.has(i)) this.setColumHiddenOrShow(i, 'collapse');
                    else this.setColumHiddenOrShow(i, 'visible');
                }
            } else {
                if (i == ChannelSheetCol.SIMPLE_CHANNEL_TYPE) {
                    this.setColumHiddenOrShow(i, 'collapse');
                } else this.setColumHiddenOrShow(i, 'visible');
            }
        }
    }

    selectedShowAlarm(key: string, event: MouseEvent) {
        event.stopPropagation();
        if (!this.selectedAlarmKeys.has(key)) {
            this.selectedAlarmKeys.set(key, alarmCols[key]);
        } else {
            this.selectedAlarmKeys.delete(key);
        }

        this.checkAlarmSettingsColumn();
    }

    checkAlarmSettingsColumn() {
        this.alarmColumns.forEach(key => {
            if (this.selectedAlarmKeys.get(key)) {
                this.setAlarmSettingShowOrHidden(alarmCols[key], 'revert');
            } else {
                this.setAlarmSettingShowOrHidden(alarmCols[key], 'collapse');
            }
        });
    }

    setAlarmSettingShowOrHidden(alarmCols, option: string) {
        this.setColumHiddenOrShow(alarmCols.enableCol, 'revert');

        for (let j = alarmCols.colStart; j < alarmCols.colEnd; j++) {
            this.setColumHiddenOrShow(j, option);
        }
    }

    setColumHiddenOrShow(index: number, option: string) {
        this.worksheets.colgroup[index].style.visibility = option;
    }

    showErrorMsg(msg: string) {
        this.toastr.error(msg, '', {
            timeOut: 4000,
            closeButton: true,
        });
    }

    async selectGroup(event: ChannelGroup, tabElement: HTMLElement) {
        if (this.isChannelUpdated) {
            const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
                width: '474px',
                data: {
                    type: DialogType.description,
                    title: '',
                    description: 'channel.group.change-select',
                },
            });
            const shouldSetSelectedChannel = await lastValueFrom(dialogRef.afterClosed());
            if (!shouldSetSelectedChannel) return;
        }

        this.worksheets.history = [];
        this.worksheets.historyIndex = -1;
        this.checkChannelSheetChange(this.worksheets);
        this.channelSheetService.setActiveGroup(event);
        this.channelSheetService.setIsChannelUpdated(false);
        this.scrollToTabIfNeeded(tabElement);
    }

    private scrollToTabIfNeeded(tab: HTMLElement) {
        if (!this.tabScrollContainer || !tab) return;

        const DEFAULT_BUTTON_WIDTH = 110;
        const RIGHT_SCROLL_PADDING = 24;
        const LEFT_SCROLL_PADDING = 16;
        const EDGE_TOLERANCE = 1;

        const container = this.tabScrollContainer.nativeElement;
        const containerRect = container.getBoundingClientRect();
        const stickyButtonWidth = this.addChannelGroupButton
            ? this.addChannelGroupButton.nativeElement.offsetWidth
            : DEFAULT_BUTTON_WIDTH;
        const visibleWidthThreshold = containerRect.width - stickyButtonWidth;

        const nextTab = tab.nextElementSibling as HTMLElement;
        const targetTabForRightScroll = nextTab && nextTab.classList.contains('channel-group-tab') ? nextTab : tab;
        const targetRectRight = targetTabForRightScroll.getBoundingClientRect();
        const relativeTargetRight = targetRectRight.right - containerRect.left;
        if (relativeTargetRight > visibleWidthThreshold) {
            const scrollAmount = relativeTargetRight - visibleWidthThreshold + RIGHT_SCROLL_PADDING;
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            return;
        }

        const prevTab = tab.previousElementSibling as HTMLElement;
        const targetTabForLeftScroll = prevTab && prevTab.classList.contains('channel-group-tab') ? prevTab : tab;
        const targetRectLeft = targetTabForLeftScroll.getBoundingClientRect();
        const relativeTargetLeft = targetRectLeft.left - containerRect.left;
        if (relativeTargetLeft < EDGE_TOLERANCE) {
            const scrollAmount = relativeTargetLeft - LEFT_SCROLL_PADDING;
            container.scrollBy({
                left: scrollAmount,
                behavior: 'smooth',
            });
        }
    }

    initSheet() {
        let self = this;
        const workSheets = jspreadsheet(this.spreadsheet.nativeElement, {
            tableOverflow: true,
            data: this.channelRows,
            columns: this.columns,
            nestedHeaders: nestedHeaders,
            contextMenu: (obj, x, y, e) => {
                let items = [];
                if (this.worksheets.getSelectedRows().length == 0) return items;
                if (this.isSystemChannelsGroup()) return items;
                if (!self.userCanEdit) return items;

                if (obj.options.allowInsertRow == true) {
                    items.push({
                        title: 'Insert rows above',
                        onclick: async function () {
                            await self.insertRows(true);
                        },
                    });

                    items.push({
                        title: 'Insert rows below',
                        onclick: async function () {
                            await self.insertRows(false);
                        },
                    });
                }
                if (obj.options.allowDeleteRow == true) {
                    items.push({
                        title: obj.options.text.deleteSelectedRows,
                        onclick: function () {
                            obj.deleteRow(obj.getSelectedRows().length ? undefined : parseInt(y));
                        },
                    });
                }

                items.push({ type: 'line' });

                items.push({
                    title: obj.options.text.copy,
                    shortcut: 'Ctrl + C',
                    onclick: function () {
                        obj.copy(true);
                    },
                });

                items.push({
                    title: obj.options.text.paste,
                    shortcut: 'Ctrl + V',
                    onclick: function () {
                        if (obj.data) {
                            obj.paste(Number(x), Number(y), obj.data);
                        }
                    },
                });

                return items;
            },
            minDimensions: [this.columns.length, 0],
            loadingSpin: true,
            allowExport: false,
            updateTable: function (instance, cell, col, row, val, label, cellName) {}.bind(this),
            toolbar: [
                {
                    id: 'undo',
                    type: 'i',
                    content: 'undo',
                    onclick: () => {
                        this.handleUndo(workSheets);
                    },
                },
                {
                    id: 'redo',
                    type: 'i',
                    content: 'redo',
                    onclick: () => {
                        this.handleRedo(workSheets);
                    },
                },
                {
                    id: 'line1',
                    type: 'i',
                    content: 'custom element',
                },
                {
                    type: 'select',

                    k: 'font-family',
                    v: ['Arial', 'Verdana', 'Montserrat'],
                    selectedValue: this.excelStylesService.DEFAULT_FAMILY_FONT,
                },
                {
                    type: 'select',
                    k: 'font-size',
                    v: ['9px', '10px', '11px', '12px', '13px', '14px', '15px', '16px', '17px', '18px', '19px', '20px'],
                    selectedValue: this.excelStylesService.DEFAULT_FONT_SIZE,
                },
                {
                    id: 'align_left',
                    type: 'i',
                    content: 'format_align_left',
                    k: 'text-align',
                    v: 'left',
                },
                {
                    id: 'align_center',
                    type: 'i',
                    content: 'format_align_center',
                    k: 'text-align',
                    v: 'center',
                },
                {
                    id: 'align_right',
                    type: 'i',
                    content: 'format_align_right',
                    k: 'text-align',
                    v: 'right',
                },
                {
                    id: 'format_bold',
                    type: 'i',
                    content: 'format_bold',
                    k: 'font-weight',
                    v: 'bold',
                },
                {
                    id: 'format_color_text',
                    type: 'i',
                    content: 'format_color_text',
                    k: 'color',
                    onclick: () => {
                        if (!this.userCanEdit) return;
                        this.textColorIsOpen = !this.textColorIsOpen;
                        this.fillColorIsOpen = false;
                    },
                },
                {
                    id: 'format_color_fill',
                    type: 'i',
                    content: 'format_color_fill',
                    k: 'background-color',
                    onclick: () => {
                        if (!this.userCanEdit) return;
                        this.fillColorIsOpen = !this.fillColorIsOpen;
                        this.textColorIsOpen = false;
                    },
                },
            ],
            ondeleterow: (worksheet, rows) => {
                this.channelRows = this.worksheets.getData();
                this.checkChannelSheetChange(this.worksheets);
                this.updateSaveButton();
            },
            onselection: (instance, x1, y1, x2, y2, origin) => {
                if (x1 === x2 && y1 === y2) {
                    const selectedValue = this.worksheets.getValueFromCoords(x1, y1);
                    const fillHandle = document.querySelector('.jexcel_corner');
                    this.channelSheetUtilsService.bindMouseEvents(fillHandle, selectedValue);
                } else {
                    this.channelSheetUtilsService.removeMouseEvents();
                }
                this.insertRowsSelectedCoord = { x1, y1, x2, y2 };
                this.keyPressEventMapping(x1, y1, x2, y2);
                this.selectionActive(x1, y1, x2, y2);
            },
            onchangestyle: () => {
                this.updateHeaderStyles();
                this.updateSaveButton();
            },
            onchange: (element, cell, colIndex, rowIndex, newValue, oldValue) => {
                this.channelSheetUtilsService.handleChange(
                    cell,
                    colIndex,
                    rowIndex,
                    newValue,
                    oldValue,
                    this.worksheets,
                    this.channelRows,
                    this.viewMode == ViewMode.SIMPLE_VIEW
                );
                const numColIndex = Number(colIndex);
                const numRowIndex = Number(rowIndex);
                if (isAlarmPanelSyncCol(numColIndex) && this.prevSelectedChannelCoord?.y1 === numRowIndex) {
                    this.channelSheetService.setUpdateChannel(this.channelRows[numRowIndex]);
                }
            },

            onafterchanges: (elem, changes) => {
                if (!changes?.length) {
                    return;
                }

                if (!(this.worksheets as any).ignoreHistory) {
                    this.channelSheetUtilsService.finalizeCustomHistory(this.worksheets);
                }

                const uniqueRows = [...new Set(changes.map(c => Number(c.row)))];
                uniqueRows.forEach(row => {
                    this.scheduleCheckDisableCells(row);
                });
                this.checkChannelSheetChange(this.worksheets);
            },

            onbeforechange: (element, cell, colIndex, rowIndex, newValue) => {
                if ((this.worksheets as any).ignoreHistory) return newValue;
                if (cell.classList.contains('readonly')) {
                    return;
                }
                const numColIndex = Number(colIndex);
                if (numColIndex === ChannelSheetCol.CHANNEL_TAG) {
                    newValue = this.channelSheetUtilsService.handleBeforeChange(newValue);
                }

                if (leadingZeroNumericColSet.has(numColIndex)) {
                    newValue = normalizeLeadingZeros(newValue);
                }

                return newValue;
            },

            onbeforepaste: (elem, copiedText, colIndex, rowIndex) => {
                if (!this.userCanEdit) {
                    return false;
                }

                const rows = copiedText.split('\n');
                const parsedRows = rows.map(row => row.split('\t'));
                const numberRows = parsedRows.length;

                const isCopyingRow = parsedRows && parsedRows[0]?.length >= this.columns.length;
                const rowUpdates: {
                    rowNumber: number;
                    newRow: any[];
                    oldRow: any[];
                }[] = [];
                if (!isCopyingRow) {
                    const cell = this.worksheets.getCell([Number(colIndex), Number(rowIndex)]);
                    if (cell?.classList.contains('readonly')) {
                        return false;
                    }
                } else {
                    const selectedRow: any = this.worksheets.getSelectedRows()[0];
                    const targetRowIndex = selectedRow ? Number(selectedRow.getAttribute('data-y')) : Number(rowIndex);

                    for (let i = 0; i < numberRows; i++) {
                        const newRow: any = _.cloneDeep(parsedRows[i]);
                        const rowNumber = targetRowIndex + i;
                        const oldRow = _.cloneDeep(this.channelRows[rowNumber]);
                        const currentRowId = (this.channelRows[rowNumber] as any)?.[0] || '';
                        newRow[ChannelSheetCol.INDEX] = currentRowId;

                        const channelType = newRow[ChannelSheetCol.CHANNEL_TYPE];
                        if (channelType) {
                            clearByChannelType(newRow, channelType, this.worksheets, rowNumber);
                        }

                        this.channelRows[rowNumber] = [...newRow] as any;
                        rowUpdates.push({ rowNumber, newRow, oldRow });
                    }

                    const root = this.worksheets.table;
                    root?.style.setProperty('display', 'none');

                    const changedRecords: any[] = [];

                    for (const { rowNumber, newRow, oldRow } of rowUpdates) {
                        if (!newRow?.length) continue;

                        for (let colNumber = 0; colNumber < newRow.length; colNumber++) {
                            const cell = this.worksheets.getCell([colNumber, rowNumber]);
                            if (!cell) continue;

                            const input = cell.firstElementChild as HTMLInputElement | null;
                            const isInput = input?.tagName === 'INPUT';

                            cell.classList.remove('readonly');
                            if (isInput) input.disabled = false;

                            if (isInput) {
                                if (newRow[colNumber] == true || newRow[colNumber] == 'true') {
                                    input.checked = true;
                                } else {
                                    input.checked = false;
                                }
                                (this.channelRows[rowNumber] as any)[colNumber] = input.checked;
                            } else {
                                cell.innerText = newRow[colNumber] ?? '';
                            }
                        }
                        this.worksheets.ignoreHistory = true;

                        try {
                            this.worksheets.setRowData(rowNumber, newRow);
                        } finally {
                            this.worksheets.ignoreHistory = false;
                        }
                        this.scheduleCheckDisableCells(Number(rowNumber));

                        for (let col = 0; col < newRow.length; col++) {
                            const oldVal = (oldRow as any)?.[col] ?? '';
                            if (newRow[col] === oldVal) continue;
                            changedRecords.push({
                                x: col,
                                y: rowNumber,
                                col,
                                row: rowNumber,
                                newValue: newRow[col],
                                oldValue: oldVal,
                            });
                        }
                    }
                    root?.style.removeProperty('display');

                    if (changedRecords.length > 0) {
                        if (!this.worksheets.history) {
                            this.worksheets.history = [];
                        }

                        const history = this.worksheets.history;
                        let historyIndex = this.worksheets.historyIndex ?? -1;

                        if (historyIndex < history.length - 1) {
                            history.splice(historyIndex + 1);
                        }
                        history.push({
                            action: 'setValue',
                            records: changedRecords,
                            selection: [],
                            custom: true,
                        });

                        this.worksheets.historyIndex = history.length - 1;
                    }

                    return false;
                }

                return copiedText;
            },
        });

        this.checkChannelSheetChange(workSheets);
        this.worksheets = workSheets;
        this.worksheets.colgroup[0].style.visibility = 'collapse';
        this.setheaderIconEvents();
    }

    checkChannelSheetChange(workSheets: jspreadsheet.JspreadsheetInstance) {
        this.updateUndoButtonState(workSheets);
        this.updateRedoButtonState(workSheets);
    }

    updateUndoButtonState(workSheets: jspreadsheet.JspreadsheetInstance): void {
        const undoBtn = document.querySelector('i#undo') as HTMLElement;
        if (!undoBtn) return;

        const history = workSheets?.history ?? [];
        const historyIndex = workSheets?.historyIndex ?? -1;
        const currentAction = history[historyIndex];

        const isEmptyStyleAction =
            currentAction?.action === 'setStyle' &&
            Object.keys(currentAction.oldValue ?? {}).length === 0 &&
            Object.keys(currentAction.newValue ?? {}).length === 0;

        const canUndo = historyIndex >= 0 && history.length > 0 && !isEmptyStyleAction;

        undoBtn.style.color = canUndo ? '#000000' : '#cccccc';
        undoBtn.style.pointerEvents = canUndo ? 'auto' : 'none';
    }
    updateRedoButtonState(workSheets: jspreadsheet.JspreadsheetInstance) {
        const redoBtn = document.querySelector('i#redo') as HTMLElement;
        if (workSheets.history?.length === 0 || workSheets.historyIndex === workSheets.history.length - 1) {
            redoBtn.style.color = '#cccccc';
            redoBtn.style.pointerEvents = 'none';
        } else {
            redoBtn.style.color = '#000000';
            redoBtn.style.pointerEvents = 'auto';
        }
    }

    async importButtonClick() {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.COMPLEX,
                title: 'channel.import.dialog.title',
                backButton: 'dlg.cancel',
                discardButton: 'channel.export.dialog.complete',
                saveButton: 'channel.export.dialog.simple',
            },
        });
        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.GO_BACK) return;
        else if (confirmationOption == ConfirmationDialogOption.SAVE) {
            this.simpleFileInput.nativeElement.click();
        } else if (confirmationOption == ConfirmationDialogOption.DISCARD) {
            this.excelFileInput.nativeElement.click();
        }
    }

    async downloadButtonClick() {
        try {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'channel.export.dialog.title',
                    backButton: 'dlg.cancel',
                    discardButton: 'channel.export.dialog.complete',
                    saveButton: 'channel.export.dialog.simple',
                },
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.GO_BACK) return;
            else if (confirmationOption == ConfirmationDialogOption.SAVE) {
                this.channelSheetService.showLoading();
                const blob = await this.channelExcelService.downloadSimpleExcelFile(
                    this.vesselService.getActiveVesselId()
                );
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `channel-config-${this.createFileNameFormat()}.xlsm`;
                link.click();
                link.remove();
            } else if (confirmationOption == ConfirmationDialogOption.DISCARD) {
                this.channelSheetService.showLoading();
                const blob = await this.channelExcelService.downloadExcelFile(this.vesselService.getActiveVesselId());
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `channel-config-${this.createFileNameFormat()}.xlsm`;
                link.click();
                link.remove();
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.channelSheetService.hideLoading();
        }
    }

    createFileNameFormat() {
        const date = new Date();
        const year = date.getFullYear().toString().slice(-2);
        const month = (date.getMonth() + 1).toString();
        const day = date.getDate().toString();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');

        const formattedString = `${year}/${month}/${day}-${hours}${minutes}${seconds}`;

        return formattedString;
    }

    async completeFileSelected(e: any) {
        const file = e.target.files[0];
        if (file) {
            this.channelSheetService.showLoading();
            try {
                const vesselId = this.vesselService.getActiveVesselId();
                const excelImportedData = await this.channelExcelService.importExcelFile(file, vesselId);
                if (excelImportedData) this.validateImportedData(excelImportedData);
            } catch (e) {
                this.channelSheetService.hideLoading();
                this.importErrorMessage = this.channelSheetService.getImportErrorMessage(
                    e.error.errorCode,
                    e.error.properties
                );
                this.importErrorDialog.setVisible(true);
            } finally {
                if (this.excelFileInput) {
                    this.excelFileInput.nativeElement.value = '';
                }
            }
        }
    }

    async simpleFileSelected(e: any) {
        const file = e.target.files[0];
        if (file) {
            this.channelSheetService.showLoading();
            try {
                const vesselId = this.vesselService.getActiveVesselId();
                const excelImportedData = await this.channelExcelService.importSimpleExcelFile(file, vesselId);
                if (excelImportedData) this.validateImportedData(excelImportedData);
            } catch (e) {
                this.channelSheetService.hideLoading();
                this.importErrorMessage = this.channelSheetService.getImportErrorMessage(
                    e.error.errorCode,
                    e.error.properties
                );
                this.importErrorDialog.setVisible(true);
            } finally {
                if (this.simpleFileInput) {
                    this.simpleFileInput.nativeElement.value = '';
                }
            }
        }
    }

    async validateImportedData(excelImportedData) {
        try {
            const { translateString, updatedImportedData } = channelsNeedUpdate(
                excelImportedData,
                this.translateSerivce
            );
            if (translateString.length > 0) {
                const dialogRef = this.dialog.open(DialogChannelGroupComponent, {
                    width: '474px',
                    data: {
                        type: DialogType.description,
                        title: '',
                        description: translateString,
                    },
                });

                this.channelSheetService.hideLoading();
                const shouldAutoModifiyAgree = await lastValueFrom(dialogRef.afterClosed());
                if (!shouldAutoModifiyAgree) return;
            }

            this.channelSheetService.showLoading();
            await this.channelExcelService.insertImportedData(
                this.vesselService.getActiveVesselId(),
                updatedImportedData
            );

            const vesselId = this.vesselService.getActiveVesselId();
            const channelGroupList = await this.channelGroupService.getList(this.vesselService.getActiveVesselId());
            await this.channelCategoryService.getList(vesselId);
            this.channelSheetService.setActiveGroup(channelGroupList[0]);
        } catch (e) {
            throw Error(e);
        } finally {
            this.channelSheetService.hideLoading();
        }
    }

    private updateHeaderStyles() {
        const selectedCellsCoords = this.worksheets.selectedCell;
        if (!selectedCellsCoords) {
            return;
        }
        const selectedCells: any[] = this.excelStylesService.getSelectedCells(selectedCellsCoords);
        let selectedCellsStyles = {};
        selectedCells.forEach(cell => {
            const style = this.worksheets.getStyle(cell);
            selectedCellsStyles[cell] = style;
        });
        this.excelStylesService.updateToolbar(selectedCellsStyles, this.spreadsheet);
        const colorTextIcon = document.getElementById('format_color_text');
        this.textColor = colorTextIcon.style.color;
        const colorFillIcon = document.getElementById('format_color_fill');
        this.fillColor = colorFillIcon.style.color;
    }

    private updateSaveButton() {
        if (!this.worksheets) {
            return;
        }

        if (this.isValidationEnabled) {
            this.validateChannels();
        }

        const currentData = this.worksheets.getData();

        if (!_.isEqual(currentData, this.initialChannelData)) {
            this.channelSheetService.setIsChannelUpdated(true);
            this.pageUpdated.emit(true);
            return;
        }

        const currentStyles = this.worksheets.getStyle();
        const hasUpdatedStyles = this.excelStylesService.areStylesUpdated(currentStyles);

        this.channelSheetService.setIsChannelUpdated(hasUpdatedStyles);
        this.pageUpdated.emit(hasUpdatedStyles);
    }

    private channelsHaveChanges() {
        const currentData = this.channelRows;
        if (!_.isEqual(currentData, this.initialChannelData)) {
            return true;
        }
        return false;
    }

    async printChannels() {
        if (this.channelsHaveChanges()) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '474px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'confirmation-dialog.print.title',
                    message: 'confirmation-dialog.print.message',
                    backButton: 'confirmation-dialog.print.back',
                    discardButton: 'confirmation-dialog.print.discard',
                    saveButton: 'confirmation-dialog.print.save',
                },
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                const hasError = await this.save();
                if (hasError) return;
            }
        }

        const vessel = this.vesselService.getActiveVessel();
        if (!vessel) return;

        try {
            await this.printChannelsService.triggerPrint(this.vessel, true);
        } catch (err) {
            console.error('Error in printSensorCableTable:', err);
            throw err;
        }
    }

    async insertRows(option: boolean) {
        if (!this.userCanEdit) return;
        if (this.prevSelectedChannelCoord) {
            const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
            this.channelSheetService.setSelectedChannel(this.channelRows[y1]);
            this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
            this.worksheets.resetSelection();
        }

        const currentData = this.worksheets.getData();

        const dialogRef = this.dialog.open(DialogInsertRowsComponent, {
            width: '474px',
            data: {
                title: option
                    ? 'dlg.channel-sheet.insert-rows.title-above'
                    : 'dlg.channel-sheet.insert-rows.title-below',
                coord: this.insertRowsSelectedCoord,
                maxNumberRows: this.MAX_CHANNEL_ROWS - currentData.length,
            },
        });

        const dialogResult = await lastValueFrom(dialogRef.afterClosed());
        if (!dialogResult) return;

        const newValues: any = new Array(87).fill('');
        newValues[2] = ChannelTypeStrings[ChannelType.BLANK];

        this.worksheets.ignoreHistory = true;
        const changedRecords: any[] = [];

        try {
            let startIndex = 0;
            if (this.insertRowsSelectedCoord?.y1 || this.insertRowsSelectedCoord?.y1 == 0) {
                startIndex = this.insertRowsSelectedCoord.y1;
                if (option && this.prevSelectedChannelCoord) {
                    this.prevSelectedChannelCoord.y1 += dialogResult;
                    this.prevSelectedChannelCoord.y2 += dialogResult;
                }
            } else {
                startIndex = this.worksheets.rows.length;
            }

            this.worksheets.insertRow(dialogResult, startIndex, option ? 1 : 0);
            const loopStart = option ? startIndex : startIndex + 1;
            const loopEnd = option ? startIndex + dialogResult : startIndex + dialogResult + 1;

            const insertArray: any[][] = [];
            for (let i = 0; i < dialogResult; i++) {
                const newRow = [...newValues];
                newRow[ChannelSheetCol.INDEX] = this.generateExcelId();
                insertArray.push(newRow);
            }
            (this.channelRows as any[][]).splice(loopStart, 0, ...insertArray);
            for (let i = loopStart; i < loopEnd; i++) {
                let cell: any = this.worksheets.getCellFromCoords(0, i);
                cell?.classList.remove('readonly');

                const newRowId = this.generateExcelId();
                newValues[0] = newRowId;
                this.worksheets.setRowData(i, newValues);

                cell?.classList.add('readonly');

                for (let col = 0; col < newValues.length; col++) {
                    changedRecords.push({
                        x: col,
                        y: i,
                        col: col,
                        row: i,
                        newValue: newValues[col],
                        oldValue: '',
                    });
                }
            }
        } finally {
            this.worksheets.ignoreHistory = false;
        }

        if (changedRecords.length > 0) {
            if (!this.worksheets.history) {
                this.worksheets.history = [];
            }

            const history = this.worksheets.history;
            let historyIndex = this.worksheets.historyIndex ?? -1;

            if (historyIndex < history.length - 1) {
                history.splice(historyIndex + 1);
            }

            history.push({
                action: 'setValue',
                records: changedRecords,
                selection: [],
                custom: true,
                isRowInsertion: true,
                insertIndex: option
                    ? (this.insertRowsSelectedCoord?.y1 ?? 0)
                    : (this.insertRowsSelectedCoord?.y1 ?? 0) + 1,
                qty: dialogResult,
            });

            this.worksheets.historyIndex = history.length - 1;
        }
    }

    toggleDropdowns(selectedDropdown: PanelState) {
        this.currentPanel = this.currentPanel === selectedDropdown ? PanelState.None : selectedDropdown;
    }

    updateTextColor(newColor) {
        let selectedCellsCoords = this.worksheets.selectedCell;
        if (!selectedCellsCoords) {
            if (!this.prevSelectedChannelCoord) {
                return;
            }
            const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
            selectedCellsCoords = [x1, y1, x2, y2];
        }
        const selectedCells: any[] = this.excelStylesService.getSelectedCells(selectedCellsCoords);
        if (!selectedCells || selectedCells.length == 0) return;
        selectedCells.forEach(cell => {
            this.worksheets.setStyle(cell, 'color', newColor, true);
        });
        const colorTextIcon = document.getElementById('format_color_text');
        colorTextIcon.style.color = newColor;
        this.textColor = newColor;
        this.textColorIsOpen = false;
        const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
        this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
    }

    updateCellColor(newColor) {
        let selectedCellsCoords = this.worksheets.selectedCell;
        if (!selectedCellsCoords) {
            if (!this.prevSelectedChannelCoord) {
                return;
            }
            const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
            selectedCellsCoords = [x1, y1, x2, y2];
        }
        const selectedCells: any[] = this.excelStylesService.getSelectedCells(selectedCellsCoords);
        if (!selectedCells || selectedCells.length == 0) return;
        selectedCells.forEach(cell => {
            this.worksheets.setStyle(cell, 'background-color', newColor, true);
        });
        const colorFillIcon = document.getElementById('format_color_fill');
        colorFillIcon.style.color = newColor;
        this.fillColor = newColor;
        this.fillColorIsOpen = false;
        const { x1, y1, x2, y2 } = this.prevSelectedChannelCoord;
        this.worksheets.updateSelectionFromCoords(x1, y1, x2, y2);
    }

    getTextColorIconElement() {
        return document.getElementById('format_color_text');
    }

    getCellColorIconElement() {
        return document.getElementById('format_color_fill');
    }

    setheaderIconEvents() {
        document.getElementById('format_color_text').addEventListener('click', function (event) {
            event.stopPropagation();
            event.preventDefault();
        });

        document.getElementById('format_color_fill').addEventListener('click', function (event) {
            event.stopPropagation();
            event.preventDefault();
        });
    }

    checkDisableCells(rowIndex: number): void {
        if (rowIndex === undefined || rowIndex === null) return;

        const rowData: any[] = this.worksheets.getRowData(rowIndex);
        if (!rowData?.length) return;

        const channelType: string = rowData[ChannelSheetCol.CHANNEL_TYPE];
        const isBlankType = isBlank(channelType);
        const isSystemChannel = channelType?.startsWith('System');

        for (let colNumber = 0; colNumber < rowData.length; colNumber++) {
            const cell = this.worksheets.getCell([colNumber, rowIndex]);
            if (!cell) continue;

            const input = cell.querySelector('input') as HTMLInputElement | null;

            if (!this.userCanEdit) {
                cell.classList.add('readonly');

                if (input) {
                    input.disabled = true;
                }

                continue;
            }

            if (
                isBlankType &&
                colNumber !== ChannelSheetCol.CHANNEL_TYPE &&
                colNumber !== ChannelSheetCol.SIMPLE_CHANNEL_TYPE
            ) {
                cell.classList.add('readonly');

                if (input) {
                    input.disabled = true;
                }

                continue;
            }

            const shouldDisable =
                shouldDisableCell(rowData, channelType, colNumber) &&
                (isSystemChannel || colNumber !== ChannelSheetCol.CHANNEL_TYPE);

            cell.classList.toggle('readonly', shouldDisable);

            if (input) {
                input.disabled = shouldDisable;
            }
        }
    }

    private scheduleCheckDisableCells(rowIndex: number): void {
        this.pendingDisableRows.add(Number(rowIndex));

        if (this.disableCellsDebounceTimer) {
            return;
        }
        this.disableCellsDebounceTimer = setTimeout(() => {
            const rows = [...this.pendingDisableRows];

            this.pendingDisableRows.clear();
            this.disableCellsDebounceTimer = null;

            for (const row of rows) {
                this.checkDisableCells(row);
            }

            this.updateSaveButton();
            this.checkChannelSheetChange(this.worksheets);
            this.channelRows = this.worksheets.getData();
            for (const row of rows) {
                this.channelSheetService.setUpdateChannel(this.channelRows[row]);

                if (this.isValidationEnabled) {
                    this.validateSingleExcelRow(this.channelRows[row], row);
                }

                if (this.prevSelectedChannelCoord && this.prevSelectedChannelCoord.y1 === row) {
                    this.channelSheetService.setSelectedChannel(parseExcelRow(this.channelRows[row]));
                }
            }
        }, 0);
    }

    generateExcelId() {
        const currentRowIds = new Set(this.worksheets.getData().map((row: any) => row[ChannelSheetCol.INDEX]));
        let combinedValue: number;
        do {
            let randomValue = Math.floor(8388607 * Math.random() + 1);
            let groupNum = this.activeGroup.rowNum & 0xff;
            combinedValue = (randomValue * 0x100) | groupNum;
        } while (currentRowIds.has(combinedValue));

        return combinedValue.toString();
    }

    isSystemChannelsGroup() {
        if (!this.activeGroup) return false;
        return this.activeGroup.rowNum == systemChannelGroupNum;
    }

    sheetIsEmpty(): boolean {
        return !this.channelRows || this.channelRows.length == 0;
    }

    toggleValidationCheckbox() {
        this.isValidationEnabled = !this.isValidationEnabled;
        this.validationButtonClicked();
    }

    validationButtonClicked() {
        this.channelSheetService.setValidationEnabled(!!this.isValidationEnabled);
        if (this.isValidationEnabled) {
            this.validateChannels();
        } else {
            this.resetErrorCells();
            this.cellErrors = [];
            this.hasCellError = false;
        }
    }

    updateSheetReadOnlyState() {
        if (!this.worksheets) return;

        const totalRows = this.channelRows.length;
        const totalCols = this.columns.length;

        const root = this.worksheets.table;
        if (root) root.style.setProperty('display', 'none');

        if (!this.userCanEdit) {
            for (let row = 0; row < totalRows; row++) {
                for (let col = 0; col < totalCols; col++) {
                    const cell = this.worksheets.getCell([col, row]);
                    if (!cell) continue;

                    const input = cell.firstElementChild as HTMLInputElement | null;
                    cell.classList.add('readonly');
                    if (input && input.tagName === 'INPUT') {
                        input.disabled = true;
                    }
                }
            }
        } else {
            this.channelRows.forEach((row, rowIndex) => {
                this.checkDisableCells(rowIndex);
            });
        }

        if (root) root.style.removeProperty('display');

        this.updateSaveButton();
        this.checkChannelSheetChange(this.worksheets);
    }

    handleUndo(worksheets: any) {
        if (!worksheets.history || worksheets.historyIndex < 0) {
            return;
        }

        const action = worksheets.history[worksheets.historyIndex];
        if (!action) {
            return;
        }

        if (action.custom && action.records?.length) {
            worksheets.ignoreHistory = true;

            try {
                if (action.isRowInsertion) {
                    worksheets.deleteRow(action.insertIndex, action.qty);
                    (this.channelRows as any[][]).splice(action.insertIndex, action.qty);
                } else {
                    const reverseRecords = [...action.records].reverse();
                    for (const record of reverseRecords) {
                        const x = Number(record.x ?? record.col);
                        const y = Number(record.y ?? record.row);
                        worksheets.setValueFromCoords(x, y, record.oldValue, true);
                    }
                }
            } finally {
                worksheets.ignoreHistory = false;
            }

            this.channelRows = worksheets.getData();
            worksheets.historyIndex--;

            if (!action.isRowInsertion) {
                const affectedRows = [...new Set<number>(action.records.map((record: any) => Number(record.row)))];
                for (const row of affectedRows) {
                    this.scheduleCheckDisableCells(row);
                    if (this.isValidationEnabled) {
                        this.validateSingleExcelRow(this.channelRows[row], row);
                    }
                    if (this.prevSelectedChannelCoord && this.prevSelectedChannelCoord.y1 === row) {
                        this.channelSheetService.setSelectedChannel(this.channelRows[row]);
                    }
                }
            }
        } else {
            worksheets.undo();
            this.channelRows = worksheets.getData();
        }

        this.checkChannelSheetChange(worksheets);
        this.updateSaveButton();
    }

    handleRedo(worksheets: jspreadsheet.JspreadsheetInstance) {
        if (!worksheets.history || worksheets.historyIndex >= worksheets.history.length - 1) {
            return;
        }

        const action = worksheets.history[worksheets.historyIndex + 1];
        if (!action) {
            return;
        }

        if (action.custom && action.records?.length) {
            worksheets.historyIndex++;
            worksheets.ignoreHistory = true;

            try {
                if (action.isRowInsertion) {
                    worksheets.insertRow(action.qty, action.insertIndex, 1);
                    for (const { x, y, newValue } of action.records) {
                        worksheets.setValueFromCoords(x, y, newValue, true);
                    }
                } else {
                    for (const { x, y, newValue } of action.records) {
                        worksheets.setValueFromCoords(x, y, newValue, true);
                    }
                }
            } finally {
                worksheets.ignoreHistory = false;
            }

            this.channelRows = worksheets.getData();

            if (!action.isRowInsertion) {
                const affectedRows = [...new Set<number>(action.records.map((record: any) => Number(record.row)))];
                for (const row of affectedRows) {
                    this.scheduleCheckDisableCells(row);
                    if (this.isValidationEnabled) {
                        this.validateSingleExcelRow(this.channelRows[row], row);
                    }
                }
            }
        } else {
            worksheets.redo();
            this.channelRows = worksheets.getData();
        }

        this.checkChannelSheetChange(worksheets);
        this.updateSaveButton();
    }
}

enum ViewMode {
    SIMPLE_VIEW,
    VIEW_ALL,
}

enum PanelState {
    None = 'none',
    Alarm = 'alarm',
    Info = 'info',
    Error = 'error',
}
